import {Component, EventEmitter, Input, LOCALE_ID, Output, SimpleChanges} from '@angular/core';
import {
  Companies,
  Notification,
  NotificationForm,
  Personen, Staff,
  WebserviceResponse
} from "../../../_shared/interfaces";
import { FormBuilder, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { ClrDatepickerModule, ClrInputModule, ClrRadioModule, ClrSelectModule, ClrStepperModule } from "@clr/angular";
import { RequestCompanySearchComponent } from "../../requests/request-company-search/request-company-search.component";
import { DatePipe, NgForOf, NgIf } from "@angular/common";
import { RouterLink } from "@angular/router";
import {
  AlertService,
  IAlertType,
  PersonsStoreService,
  StaffStoreService
} from "../../../_shared/services";
import {TranslocoDirective, TranslocoService} from "@jsverse/transloco";
import { ClarityIcons, twoWayArrowsIcon } from "@cds/core/icon";
import { environment as env } from "../../../../environments/environment";
import { Subject } from "rxjs";

import { CountrySelectComponent } from '../../../_shared/components/country-select/country-select.component';
import { sichtbareMitarbeiter } from "../../../_shared/utils/staff-selection";

@Component({
  selector: 'app-notification-form',
  standalone: true,
  imports: [
    CountrySelectComponent,
    ReactiveFormsModule,
    ClrStepperModule,
    ClrInputModule,
    RequestCompanySearchComponent,
    ClrDatepickerModule,
    DatePipe,
    NgIf,
    RouterLink,
    ClrRadioModule,
    ClrSelectModule,
    NgForOf,
    TranslocoDirective
  ],
  templateUrl: './notification-form.component.html',
  styleUrl: './notification-form.component.sass',
  providers: [{ provide: LOCALE_ID, useValue: 'de' }]
})
export class NotificationFormComponent {
  persons: Personen[] = [];
  /** Ungefilterte Antwort des Servers. Fuer die Anzeige siehe `staff`. */
  staffAll: Staff[] = [];

  /**
   * Nur aktive Mitarbeiter -- und zusaetzlich der aktuell zugewiesene, auch
   * wenn er passiv ist. Siehe `sichtbareMitarbeiter`; ohne diese Ausnahme
   * verloere das Bearbeiten eines Altvorgangs seinen Bearbeiter.
   */
  get staff(): Staff[] {
    return sichtbareMitarbeiter(this.staffAll, this.notificationForm);
  }
  notificationForm!: FormGroup;

  @Input() projectId: string = '';
  @Input() jobId: number = 0;
  @Input() notificationId: number = 0;
  @Input() notificationData?: Notification;
  @Input() isUpdateMode = true;
  @Output() save = new EventEmitter<NotificationForm>();

  devMode: boolean;

  private destroy$ = new Subject<void>();

  constructor(
    private personsService: PersonsStoreService,
    private staffService: StaffStoreService,
    private alertDispatcher: AlertService,
    private fb: FormBuilder,
    private datePipe: DatePipe,
    private translocoService: TranslocoService,
  ) {
    ClarityIcons.addIcons(twoWayArrowsIcon);
    this.devMode = env.stages.dev;
    this.initForm();
  }

  onSubmit() {
    const payload: NotificationForm = this.notificationForm.value;
    payload.stepI.country = this.normalizeCountryForBackend(payload.stepI.country);
    this.save.emit(payload);
  }

  ngOnInit() {
    this.notificationForm.patchValue({
      stepI: {
        project_id: this.projectId,
        job_id: this.jobId,
        notification_id: this.notificationId,
      }
    });
    this.loadStaff();
    if (!this.notificationData?.cid) {
      this.loadPersons(parseInt(this.splitProjectNumber(this.projectId, 0)));
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['notificationData'] && this.notificationData) {
      this.patchFormValues(this.notificationData);
      if (this.notificationData.cid) {
        this.loadPersons(this.notificationData.cid);
      }
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initForm() {
    this.notificationForm = this.fb.group({
      stepI: this.fb.group({
        city:  [''],
        company_name:  [''],
        country:  [''],
        description:  [''],
        job_id: 0,
        name_project:  [''],
        notification_date:  this.currentDate(),
        notification_id: 0,
        notification_type: 0,
        order_id:  [''],
        order_pps: 0,
        postcode:  [''],
        project_id:  [''],
        purchasing_id:  [''],
        street:  [''],
        status:  [''],
        subject:  [''],
      }),
      stepII: this.fb.group({
        employee_phm: 0
      }),
      stepIII: this.fb.group({
        employee_leader: 0
      })
    })
  }

  preFillRow(supplier: Companies) {
    this.notificationForm = this.fb.group({
      stepI: this.fb.group({
        city: supplier.city,
        company_name: supplier.company,
        country: this.mapCountryForForm(supplier.country),
        description: this.notificationData?.description,
        job_id: this.notificationData?.job_id,
        name_project: this.notificationData?.name_project,
        notification_date:  this.currentDate(),
        notification_id: this.notificationData?.notification_id,
        notification_type: this.notificationData?.notification_type,
        order_id: this.notificationData?.order_id,
        order_pps: this.notificationData?.order_pps,
        postcode: supplier.postcode,
        project_id: this.notificationData?.project_id,
        purchasing_id: this.notificationData?.purchasing_id,
        street: supplier.street,
        status: 'open',
        subject: this.notificationData?.subject,
      }),
      stepII: this.fb.group({
        employee_phm: this.notificationData?.employee_phm
      }),
      stepIII: this.fb.group({
        employee_leader: this.notificationData?.employee_leader
      })
    });
    this.loadPersons(supplier.cid)
  }

  private patchFormValues(data: Notification) {
    if (!data) return;

    const formValue = {
      stepI: {
        city: data.city ?? '',
        company_name: data.company_name ?? '',
        country: this.mapCountryForForm(data.country),
        description: data.description ?? '',
        job_id: data.job_id ?? 0,
        name_project: data.name_project ?? '',
        notification_date: this.datePipe.transform(data.notification_date, 'dd.MM.yyyy') ?? this.currentDate(),
        notification_id: data.notification_id ?? 0,
        notification_type: data.notification_type ?? 0,
        order_id: data.order_id ?? '',
        order_pps: data.order_pps ?? 0,
        postcode: data.postcode ?? '',
        project_id: data.project_id ?? this.projectId,
        purchasing_id: data.purchasing_id ?? '',
        street: data.street ?? '',
        status: data.status ?? '',
        subject: data.subject ?? '',
      },
      stepII: {
        employee_phm: data.employee_phm ?? 0,
      },
      stepIII: {
        employee_leader: data.employee_leader ?? 0,
      }
    };
    this.notificationForm.patchValue(formValue);
  }

  private mapCountryForForm(country?: string | null): string {
    if (!country) return '';
    const trimmed = country.trim();
    if (!trimmed) return '';
    const upper = trimmed.toUpperCase();
    if (upper === 'DE' || trimmed === 'Deutschland' || trimmed === 'Germany') return 'DE';
    if (upper === 'AT' || trimmed === 'Österreich' || trimmed === 'Austria') return 'AT';
    if (upper === 'CH' || trimmed === 'Schweiz' || trimmed === 'Switzerland') return 'CH';
    return trimmed;
  }

  private normalizeCountryForBackend(country?: string | null): string {
    if (!country) return '';
    const trimmed = country.trim();
    if (!trimmed) return '';
    const upper = trimmed.toUpperCase();
    if (upper === 'DE' || trimmed === 'Deutschland' || trimmed === 'Germany') return 'DE';
    if (upper === 'AT' || trimmed === 'Österreich' || trimmed === 'Austria') return 'AT';
    if (upper === 'CH' || trimmed === 'Schweiz' || trimmed === 'Switzerland') return 'CH';
    return trimmed;
  }

  private currentDate(): string {
    return this.datePipe.transform(new Date(), 'dd.MM.yyyy') ?? '';
  }

  splitProjectNumber(projectId: string, substring: number) {
    var splits = projectId.split('.');
    return splits[substring];
  }

   private loadPersons(companyId: number) {
    this.personsService.getPersonInCompany(companyId, true).subscribe({
      next: (response: WebserviceResponse<Personen[]>) => {
        if (response?.object) {
          this.persons = response.object;
        }
      },
      complete: () => {
        // Sortiere die Liste nach dem Nachnamen
        this.persons.sort((a, b) => {
          if (a.lastname < b.lastname) {
            return -1;
          }
          if (a.lastname > b.lastname) {
            return 1;
          }
          return 0;
        });

      },
      error: (error: any) => {
        this.alertDispatcher.show({
          message: 'Fehler beim Laden der Kunden: ' + error,
          clrAlertType: IAlertType.DANGER
        }, 5000);
        return
      }
    });
  }

  projectStaff(): number {
    return this.notificationForm.get('stepII')?.get('employee_phm')?.value || 0;
  }

  private loadStaff() {
    this.staffService.getStaff().subscribe({
      next: (response: WebserviceResponse<Staff[]>) => {
        if (response?.object) {
          this.staffAll = response.object;
        }
      },
      error: (error: any) => {
        this.alertDispatcher.show({
          message: 'Fehler beim Laden der Mitarbeiter: ' + error,
          clrAlertType: IAlertType.DANGER
        }, 5000);
      }
    });
  }
}
